// Streaming-markdown stabilizer (campaign spec 2026-07-31-query-spa-surface §4.4, unit S4-0).
//
// The Ask answer streams token-by-token and the WHOLE partial buffer is re-parsed as markdown on every
// token. Most markdown is stable under that (a growing paragraph renders as a growing paragraph), but a
// handful of block constructs are not: a GFM table is a paragraph of literal `| a | b |` pipes until its
// delimiter row arrives, then reflows; a half-typed closing code fence renders as content inside the code
// block; a bare `-` flashes an empty bullet. The first table a user ever sees rendering as pipe soup reads
// as a bug — and that perception is what gets format-permitting prompt changes reverted (spec §4.4).
//
// splitStreamingTail(buffer) returns { stable, held }: `stable` is safe to parse and render now; `held` is
// the trailing fragment currently inside an unstable block, kept OUT of the parse until the block closes.
// Invariants:
//   - stable + held === buffer, always — this only ever WITHHOLDS a suffix, it never rewrites.
//   - held is transient: it is re-derived from the full buffer on every token, so as soon as the block
//     completes (delimiter row lands, fence closes, marker gains content) the fragment renders. On any
//     non-streaming render the caller must pass the full buffer straight through — a misjudged hold can
//     therefore only ever delay paint by a few tokens, never lose content.
//
// Deliberately NOT held: a growing prose paragraph/list item/heading with content (live streaming is the
// point), and an OPEN code fence's body — CommonMark runs an unclosed fence to end-of-input, so a growing
// code block renders correctly while open; only a partial CLOSING fence line needs holding.

interface Split {
  stable: string;
  held: string;
}

// A fence opener: ``` or ~~~ (3+, up to 3 leading spaces), optionally with an info string.
const FENCE_OPEN = /^ {0,3}(`{3,}|~{3,})/;
// A line that could be, or could grow into, a closing fence: nothing but backticks/tildes + spaces.
const FENCE_CLOSE_PREFIX = /^ {0,3}(`+|~+)[ \t]*$/;
// A list/quote/heading marker with no content yet — renders as an empty bullet / bare `#` if parsed now.
const BARE_MARKER = /^ {0,3}(?:[-*+]|\d{1,9}[.)]|>|#{1,6})[ \t]*$/;
// A line of nothing but - = _ * (+ spaces): a thematic break or setext underline in progress. Parsing it
// early either flashes literal dashes or — worse for `---`'s prefix `--` under a paragraph — briefly
// reflows the paragraph above into a setext heading. Also catches a bare `**` (bold in progress).
const RULE_IN_PROGRESS = /^ {0,3}[-=_*][-=_* \t]*$/;
// A table delimiter row: pipes, dashes, colons, spaces — with at least one dash.
const TABLE_DELIMITER = /^ {0,3}\|?[ \t:|-]*-[ \t:|-]*$/;
// A table-ish line: starts with a pipe (the shape LLMs emit; GFM allows pipe-less rows but the model
// doesn't write them, and a false negative here only means a few tokens of pipe-flash, not lost content).
const TABLE_LINE = /^ {0,3}\|/;

export function splitStreamingTail(buffer: string): Split {
  if (buffer === "") return { stable: "", held: "" };

  const lines = buffer.split("\n");
  const endsWithNewline = buffer.endsWith("\n");
  // With a trailing newline, split() leaves a final "" — drop it so `lines` is exactly the real lines,
  // and remember there is no partial line.
  if (endsWithNewline) lines.pop();
  const partial = endsWithNewline ? null : lines[lines.length - 1];
  const completeLines = endsWithNewline ? lines : lines.slice(0, -1);

  // Fence state across the COMPLETE lines (the partial line is judged against this state, not part of it).
  let fence: { char: string; len: number } | null = null;
  for (const line of completeLines) {
    if (fence) {
      const m = line.match(FENCE_CLOSE_PREFIX);
      if (m && m[1][0] === fence.char && m[1].length >= fence.len) fence = null;
    } else {
      const m = line.match(FENCE_OPEN);
      if (m) fence = { char: m[1][0], len: m[1].length };
    }
  }

  const holdFromLine = (index: number): Split => {
    // Everything from line `index` on is held; the newline BEFORE it stays in stable.
    const stable = lines.slice(0, index).join("\n");
    return { stable: stable === "" ? "" : stable + "\n", held: buffer.slice(stable === "" ? 0 : stable.length + 1) };
  };

  if (fence) {
    // Inside an open fence the body streams as-is. Hold only a partial line that could be the closing
    // fence (any run of the fence char — it may still be growing toward the required length).
    if (partial !== null) {
      const m = partial.match(FENCE_CLOSE_PREFIX);
      if (m && m[1][0] === fence.char) return holdFromLine(lines.length - 1);
    }
    return { stable: buffer, held: "" };
  }

  // Trailing block: lines after the last blank COMPLETE line (plus the partial line, if any).
  let blockStart = 0;
  for (let i = completeLines.length - 1; i >= 0; i--) {
    if (completeLines[i].trim() === "") {
      blockStart = i + 1;
      break;
    }
  }
  const block = lines.slice(blockStart);

  // Table handling. If the trailing block opens with a pipe row, it is a table being born:
  //   - no delimiter row yet → the whole block is literal pipe soup → hold the whole block;
  //   - delimiter row present (line 2 of the block) → the table renders; hold only a partial trailing
  //     row, which would otherwise paint as a malformed row that pops cells in one by one.
  if (block.length > 0 && TABLE_LINE.test(block[0])) {
    const delimiterLive = block.length >= 2 && !partialIs(block, partial, 1) && TABLE_DELIMITER.test(block[1]);
    if (!delimiterLive) return holdFromLine(blockStart);
    if (partial !== null && partial !== "") return holdFromLine(lines.length - 1);
    return { stable: buffer, held: "" };
  }

  if (partial !== null && partial !== "") {
    // A fence opener arriving: hold both the not-yet-3 run of fence chars and a full opener (an opener
    // with nothing after it renders an empty code block that immediately restyles).
    if (FENCE_OPEN.test(partial) || /^ {0,3}(`{1,2}|~{1,2})$/.test(partial)) return holdFromLine(lines.length - 1);
    if (BARE_MARKER.test(partial) || RULE_IN_PROGRESS.test(partial)) return holdFromLine(lines.length - 1);
  }

  return { stable: buffer, held: "" };
}

// Is block[i] actually the (unfinished) partial line? True when the block's i-th line is the buffer's
// final, newline-less line — a delimiter row still being typed must not count as live.
function partialIs(block: string[], partial: string | null, i: number): boolean {
  return partial !== null && i === block.length - 1;
}
